import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss',
})
export class LoginPageComponent {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  loading = false;
  errorMessage = '';
  infoMessage = '';
  readonly publicWebsiteBaseUrl = this.resolvePublicWebsiteBaseUrl();

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  submit(): void {
    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.infoMessage = '';

    this.auth.login(this.form.getRawValue() as { email: string; password: string }).subscribe({
      next: (profile) => {
        if (this.auth.hasAdminAccess(profile)) {
          this.loading = false;
          this.router.navigate(['/dashboard/stats']);
          return;
        }
        this.auth.logout();
        this.errorMessage = 'Accès réservé aux admins et managers.';
        this.loading = false;
      },
      error: (error) => {
        this.errorMessage = this.toErrorMessage(error);
        this.loading = false;
      },
    });
  }

  requestPasswordReset(): void {
    const emailControl = this.form.controls.email;
    if (emailControl.invalid || this.loading) {
      emailControl.markAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.infoMessage = '';

    this.auth.requestPasswordReset(emailControl.getRawValue() ?? '').subscribe({
      next: (response) => {
        this.infoMessage =
          response.message ?? 'Si un compte existe, un email de réinitialisation a été envoyé.';
        this.loading = false;
      },
      error: (error) => {
        this.errorMessage = this.toErrorMessage(error);
        this.loading = false;
      },
    });
  }

  private toErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    if (error instanceof HttpErrorResponse) {
      const backendMessage =
        typeof error.error === 'object' && error.error
          ? (error.error as { message?: string }).message
          : undefined;

      if (error.status === 401) {
        return 'Email ou mot de passe invalide.';
      }
      if (error.status === 0) {
        return 'Réseau indisponible, vérifie ta connexion.';
      }
      return backendMessage ?? 'Connexion impossible.';
    }

    return 'Connexion impossible.';
  }

  private resolvePublicWebsiteBaseUrl(): string {
    if (typeof window === 'undefined') {
      return 'https://afrotchop.afrimoov.com';
    }

    const { protocol, hostname } = window.location;
    if (hostname === 'afrotchop-admin-dev.afrimoov.com' || hostname === 'admin-dev.afrifood.fr') {
      return 'https://afrotchop-dev.afrimoov.com';
    }
    if (hostname === 'afrotchop-admin.afrimoov.com' || hostname === 'admin.afrifood.fr') {
      return 'https://afrotchop.afrimoov.com';
    }
    if (hostname.startsWith('admin-dev.')) {
      return `${protocol}//${hostname.replace(/^admin-dev\./, 'dev.')}`;
    }
    if (hostname.startsWith('admin.')) {
      return `${protocol}//${hostname.replace(/^admin\./, '')}`;
    }
    if (hostname.includes('localhost')) {
      return `${protocol}//localhost:4200`;
    }

    return `${protocol}//${hostname}`;
  }
}
